<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useSessionStore } from '@Stores';
import { INTAKE_TYPES } from '@IntakeModule';
import { useCartSteps } from '../../../composables';
import { isCheckInIntakeType } from '../../../helpers';
import useCartStore from '../../../store';
import { CART_STEPS, OUTCOMES_TYPES } from '../../../types';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { cartData, isCitation, isIntake, isProduct, isReturnToOwner, outcomeCheckoutType, outcomeType, stepState } =
  storeToRefs(useCartStore());
const { shelterHasProducts, shelterProfile } = storeToRefs(useSessionStore());

const {
  ADD_PEOPLE_STEP_HEADERS,
  OUTCOMES_CART_MANUAL_STEPS,
  OUTCOMES_CART_MANUAL_WITH_PRODUCTS_STEPS,
  OUTCOMES_CART_MOBILE_STEPS,
  OUTCOMES_CART_MOBILE_WITH_PRODUCTS_STEPS,
  PRODUCTS_CART_MANUAL_STEPS,
  PRODUCTS_CART_MOBILE_STEPS,
  INTAKE_CART_MANUAL_STEPS,
  INTAKE_CART_MOBILE_STEPS,
  nextStep,
  currentStep,
  previousStep,
  setOutcomeFlow,
} = useCartSteps();

const { ADD_PEOPLE, CHECKOUT_METHOD, DONATION, MEDICAL_COSTS, MOBILE_CHECKOUT, PAYMENT_METHOD } = CART_STEPS;
const { ADOPTED, CLINIC_OUT, OWNER_REQUESTED_EUTHANASIA, RETURN_TO_OWNER, SERVICE_OUT } = OUTCOMES_TYPES;

const checkoutType = ref<CART_STEPS | null>(null);
const showErrorMessage = ref(false);

const isCheckInType = computed(() => isCheckInIntakeType(outcomeType.value));
// prettier-ignore
const mobileTitle = computed(() => isCheckInType.value ? 'cart.checkoutSelector.mobile.titleCheckin'
  : isIntake.value ? 'cart.checkoutSelector.mobile.titleIntake'
  : 'cart.checkoutSelector.mobile.title');
// prettier-ignore
const mobileLabel = computed(() => isCheckInType.value ? 'cart.checkoutSelector.mobile.labelCheckin'
  : isIntake.value ? 'cart.checkoutSelector.mobile.labelIntake'
  : 'cart.checkoutSelector.mobile.label');
// prettier-ignore
const manualTitle = computed(() => isCheckInType.value ? 'cart.checkoutSelector.manual.titleCheckin'
  : isIntake.value ? 'cart.checkoutSelector.manual.titleIntake'
  : 'cart.checkoutSelector.manual.title');
// prettier-ignore
const manualLabel = computed(() => isCheckInType.value ? 'cart.checkoutSelector.manual.labelCheckin'
  : isIntake.value ? 'cart.checkoutSelector.manual.labelIntake'
  : 'cart.checkoutSelector.manual.label');
const isRetroactivePayment = computed(() => cartData.value?.paid_date);
const nextStepIcon = computed(() => nextStep.value.step?.header.icon || 'faMobile');
const showMobileCheckoutOption = computed(() => !isRetroactivePayment.value);
const isManualCheckout = computed(
  () => checkoutType.value && [DONATION, ADD_PEOPLE, PAYMENT_METHOD].includes(checkoutType.value)
);

onMounted(() => (currentStep.value = CHECKOUT_METHOD));

watch(checkoutType, () => onCheckoutTypeUpdate());

// HELPERS
function getPeopleStepType(): keyof typeof ADD_PEOPLE_STEP_HEADERS | undefined {
  // prettier-ignore
  return isCitation.value ? 'citation'
       : isReturnToOwner.value ? 'guardian'
       : isProduct.value ? 'product'
       : isIntake.value ? 'contact'
       : [CLINIC_OUT, OWNER_REQUESTED_EUTHANASIA, SERVICE_OUT].includes(outcomeType.value) ? 'guardian'
       : 'adopter';
}

// EVENTS
function onCheckoutTypeUpdate() {
  showErrorMessage.value = false;

  const isTransferIn = INTAKE_TYPES.TRANSFER_IN === outcomeType.value;
  const manualIntakeFlow = isTransferIn ? INTAKE_CART_MANUAL_STEPS.slice(1) : INTAKE_CART_MANUAL_STEPS;
  const mobileIntakeFlow = isTransferIn ? INTAKE_CART_MOBILE_STEPS.slice(1) : INTAKE_CART_MOBILE_STEPS;

  // prettier-ignore
  const manualFlow = isProduct.value ? PRODUCTS_CART_MANUAL_STEPS :
        isIntake.value ? manualIntakeFlow :
        shelterHasProducts.value ? OUTCOMES_CART_MANUAL_WITH_PRODUCTS_STEPS :
        OUTCOMES_CART_MANUAL_STEPS;

  // prettier-ignore
  const mobileFlow = isProduct.value ? PRODUCTS_CART_MOBILE_STEPS :
        isIntake.value ? mobileIntakeFlow :
        shelterHasProducts.value ? OUTCOMES_CART_MOBILE_WITH_PRODUCTS_STEPS : 
        OUTCOMES_CART_MOBILE_STEPS;

  let activeFlow = isManualCheckout.value ? manualFlow : mobileFlow;

  // NOTE :: When donations are disabled for the shelter, remove donation step from flow
  if (!shelterProfile.value?.donations) {
    activeFlow = activeFlow.filter((step) => step !== DONATION);
  }

  // NOTE :: Preserve medical costs step if it exists in current flow, or add it if conditions are met
  const shelterIsTrackingMedicalCosts = shelterProfile.value?.track_medical_costs;
  const OUTCOMES_CART_TYPES = [ADOPTED, CLINIC_OUT, RETURN_TO_OWNER, SERVICE_OUT];
  const isCartOutcome =
    !isProduct.value && !isIntake.value && !isCitation.value && OUTCOMES_CART_TYPES.includes(outcomeType.value);
  const shouldHaveMedicalStep = shelterIsTrackingMedicalCosts && isCartOutcome;
  const hasMedicalStepInFlow = activeFlow.includes(MEDICAL_COSTS);

  // If medical step should exist but isn't in the new flow, add it
  if (shouldHaveMedicalStep && !hasMedicalStepInFlow) {
    const checkoutMethodIndex = activeFlow.findIndex((step) => step === CHECKOUT_METHOD);
    if (checkoutMethodIndex > 0) {
      activeFlow = [...activeFlow];
      activeFlow.splice(checkoutMethodIndex - 1, 0, MEDICAL_COSTS);
    }
  }

  const peopleStepType = getPeopleStepType();

  setOutcomeFlow(activeFlow, peopleStepType);
}

function onNextStep() {
  if (!checkoutType.value) return (showErrorMessage.value = true);

  stepState.value![CART_STEPS.CHECKOUT_METHOD] = checkoutType.value;
  outcomeCheckoutType.value = checkoutType.value;
  emits('onStepUpdate', checkoutType.value);
}
</script>

<template>
  <div class="flex h-fit flex-col items-start justify-center gap-4">
    <div class="flex flex-1 flex-wrap justify-center gap-4 sm:justify-start">
      <div
        v-if="showMobileCheckoutOption"
        class="w-44"
      >
        <Button
          :class="[
            'w-full !border !border-solid border-brand-500 transition-colors',
            checkoutType !== MOBILE_CHECKOUT ? 'af-button-success-outlined' : 'af-button-success',
          ]"
          :outlined="checkoutType !== MOBILE_CHECKOUT"
          @click="checkoutType = MOBILE_CHECKOUT"
        >
          <div class="flex flex-col gap-3 py-4">
            <FaIcon
              class="text-3xl"
              :icon="$icons.faMobile"
            />

            <p>
              {{ $t(mobileTitle) }}
            </p>

            <p class="text-center text-xs">
              {{ $t(mobileLabel) }}
            </p>
          </div>
        </Button>
      </div>

      <div class="w-44">
        <Button
          :class="[
            'w-full !border !border-solid border-brand-500 transition-colors',
            isManualCheckout ? 'af-button-success' : 'af-button-success-outlined',
          ]"
          :outlined="!isManualCheckout"
          @click="checkoutType = !isIntake ? ADD_PEOPLE : shelterProfile?.donations ? DONATION : PAYMENT_METHOD"
        >
          <div class="flex flex-col gap-3 py-4">
            <FaIcon
              class="text-3xl"
              :icon="$icons.faUser"
            />

            <p>
              {{ $t(manualTitle) }}
            </p>

            <p class="text-center text-xs">
              {{ $t(manualLabel) }}
            </p>
          </div>
        </Button>
      </div>
    </div>

    <footer class="w-full flex-shrink">
      <Message
        v-if="showErrorMessage"
        pt:text="text-sm"
        severity="error"
      >
        {{ $t('cart.checkoutSelector.error') }}
      </Message>

      <div class="justfiy-between flex">
        <Button
          v-if="previousStep"
          outlined
          class="af-button af-button-success-outlined w-fit"
          icon="pi pi-arrow-left"
          :label="$t('common.back')"
          @click="emits('onStepUpdate', previousStep)"
        />

        <Button
          class="af-button af-button-success ml-auto"
          iconPos="right"
          :disabled="showErrorMessage"
          :label="$t('common.nextStep')"
          @click="onNextStep"
        >
          <template #icon>
            <FaIcon
              class="mr-2"
              :icon="$icons[nextStepIcon]"
            />
          </template>
        </Button>
      </div>
    </footer>
  </div>
</template>
